import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as ReTooltip } from 'recharts'
import { AlertTriangle } from 'lucide-react'
import { useCurrency } from '@/contexts/CurrencyContext'
import { compactCurrency } from '@/components/admin/dashboard/dashboardFormat'
import { chartTooltipPie } from '@/lib/chartTheme'
import type { AdvancedStockReport } from '@/types'
import * as Help from '@/lib/inventory-report-help'
import { MetricHint, fmtQtyUnit, type CountTarget } from './primitives'

const DONUT_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16']

/**
 * "What's my stock worth right now?" — the headline valuation, its category
 * split (same total, so the slices always sum to the headline), and a flag for
 * impossible negative quantities with a one-tap fix (count the item).
 */
export function StockNowCard({ stockNow, onCountItem }: {
  stockNow: AdvancedStockReport['stock_now']
  onCountItem: (item: CountTarget) => void
}) {
  const { formatCurrency } = useCurrency()
  const cats = stockNow.categories ?? []
  const negatives = stockNow.negative_items ?? []

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <CardTitle className="text-base font-semibold">What’s my stock worth right now?</CardTitle>
          <MetricHint text={Help.STOCK_NOW_VALUE} label="stock value" />
        </div>
        <CardDescription className="text-xs">
          Everything on your shelves, valued at what it cost you to buy.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <p className="text-3xl font-bold tabular-nums" title={formatCurrency(stockNow.total_value)}>
          {compactCurrency(stockNow.total_value)}
        </p>

        {negatives.length > 0 && (
          <div className="mt-3 rounded-md border border-warning-border bg-warning-surface/40 px-3 py-2 text-sm">
            <div className="flex items-center gap-2 font-medium text-warning-fg">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              {negatives.length} item{negatives.length === 1 ? ' shows' : 's show'} less than zero
              <MetricHint text={Help.STOCK_NOW_NEGATIVE} label="negative stock" />
            </div>
            <ul className="mt-1.5 space-y-1">
              {negatives.slice(0, 5).map(n => (
                <li key={n.item_id} className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
                  <span className="truncate">{n.name} — {fmtQtyUnit(n.on_hand, n.unit)}</span>
                  <button
                    type="button"
                    className="shrink-0 rounded border px-2 py-0.5 font-medium text-foreground hover:bg-muted"
                    onClick={() => onCountItem({ id: n.item_id, name: n.name, unit: n.unit, quantity_on_hand: n.on_hand })}
                  >
                    Count it
                  </button>
                </li>
              ))}
            </ul>
            {negatives.length > 5 && (
              <p className="mt-1 text-[11px] text-muted-foreground">+{negatives.length - 5} more under “What needs your attention”.</p>
            )}
          </div>
        )}

        {cats.length > 0 && (
          <div className="mt-4 flex items-center gap-4">
            <ResponsiveContainer width="45%" height={200}>
              <PieChart>
                <Pie data={cats} dataKey="value" nameKey="name" cx="50%" cy="50%"
                  innerRadius={52} outerRadius={84} paddingAngle={2} strokeWidth={0}>
                  {cats.map(c => (
                    <Cell key={c.category_id ?? c.name} fill={DONUT_COLORS[cats.indexOf(c) % DONUT_COLORS.length]} />
                  ))}
                </Pie>
                <ReTooltip {...chartTooltipPie} formatter={(val: number) => formatCurrency(val)} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex-1 space-y-1.5 text-sm min-w-0">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                By category
                <MetricHint text={Help.STOCK_NOW_DONUT} label="category split" />
              </div>
              {cats.slice(0, 8).map((c, i) => (
                <div key={c.category_id ?? c.name} className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }} />
                  <span className="truncate flex-1">{c.name}</span>
                  <span className="text-muted-foreground tabular-nums text-xs" title={formatCurrency(c.value)}>
                    {c.pct >= 0.5 ? `${c.pct.toFixed(0)}%` : '<1%'}
                  </span>
                </div>
              ))}
              {cats.length > 8 && (
                <p className="text-[11px] text-muted-foreground">+{cats.length - 8} smaller categories</p>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
